import { PeopleCollection } from './common/services/people-collection';
import { Realtime, RealtimeEventType, RealtimeRole, RealtimeStatus, } from '@doxyme/og-realtime';
import { RootScopeEvents } from '../common/services/root-scope-types';
import { extend, get, isEmpty } from 'lodash';
import { clear, forceRemove, onJoin, onLeave, onStateUpdate, PEOPLE_TYPES, } from '../store/features/realtime/presence';
import { reset, subscribe } from '../store/features/realtime/user';
import EventEmitter from '../utility/event-emitter';
import { getPersonTypeFromIdentity } from './helpers/person';
import { encoding, utils } from '../modules/core';
import { REALTIME_COMPONENT_NAMES } from '../store/features/realtime/messages/types';
import { setExtensionMessage } from '../store/features/messages';
import { PnEvents, DEBOUNCE_TIMEOUT } from './constants';
import { PnPerson } from './common/services/pn-person.class';
import { setApolloSessionStorageData } from 'utility/sessionStorage/actions';
import { shareWithApollo } from '../utility/shareWithApollo';
import config from '../global/env.config';
import { API } from 'vendor/api';
import { RealtimeDebugger } from './realtime-debugger';
const realtimeDebugger = new RealtimeDebugger();
function debugMembers(realtimeMembersMap) {
    const members = Object.values(realtimeMembersMap);
    for (const member of members) {
        realtimeDebugger.patientJoin(member.identity);
    }
}
export class PnService extends EventEmitter {
    /* @ngInject */
    constructor($log, $rootScope, $ngRedux, doxyVersion, growthbookFactory) {
        super();
        this.$rootScope = $rootScope;
        this.$ngRedux = $ngRedux;
        this.doxyVersion = doxyVersion;
        this.growthbookFactory = growthbookFactory;
        this.connected = false;
        this.globalState = {};
        this.initData = {};
        this.logError = $log.error.bind($log, 'realtime:');
        this.debug = $log.debug.bind($log, 'realtime:');
        this.resetState();
        this._setPicture = utils.debounceAsync(this._setPicture.bind(this), DEBOUNCE_TIMEOUT);
        this._setState = utils.debounceAsync(this._setState.bind(this), DEBOUNCE_TIMEOUT);
        for (const member of shareWithApollo.getDataForLegacy().members ?? []) {
            this.createAllJoinedPerson(member);
        }
    }
    async initProvider(userId, token, roomIds, state) {
        await this.initRealtime(RealtimeRole.Provider, roomIds.map((id) => `${id}`), { userId: String(userId), token }, state);
    }
    async initPatient(roomId, state, passcode) {
        await this.initRealtime(RealtimeRole.Patient, [`${roomId}`], { passcode }, state);
        if (passcode) {
            setApolloSessionStorageData({ role: RealtimeRole.Patient, passcode });
        }
    }
    getUuid() {
        return get(this.realtime, 'identity.id');
    }
    getUserId() {
        return this.getIdentityUserId() || this.getUuid();
    }
    getIdentityUserId() {
        return get(this.realtime, 'identity.userId');
    }
    getUserType() {
        if (!this.realtime || !this.realtime.identity) {
            return;
        }
        return getPersonTypeFromIdentity(this.realtime.identity);
    }
    getPersonUserId(person) {
        if (person) {
            return this.getPersonIdentityUserId(person) || person.uuid;
        }
    }
    getPersonIdentityUserId(person) {
        return get(person, 'member.identity.userId');
    }
    isDoctor() {
        return this.getUserType() === PEOPLE_TYPES.doctor;
    }
    isPatient() {
        return this.getUserType() === PEOPLE_TYPES.patient;
    }
    isPersonOnline(person) {
        if (!person) {
            return false;
        }
        if (person.isDoctor()) {
            return Boolean(this.people.models.find((p) => person.getUserId() === p.getUserId()));
        }
        return Boolean(this.people.findByUid(person.uuid));
    }
    enableMediaAccess() {
        this.setState({ onlyChat: false });
    }
    disableMediaAccess() {
        if (this.globalState.onlyChat) {
            return;
        }
        this.setState({ onlyChat: true });
    }
    async setUserPictureFromCamera(url) {
        this.debug('setUserPictureFromCamera', Boolean(url));
        if (!url || this.userPictureFromAvatar || this.userPicture === url) {
            return;
        }
        this.userPicture = url;
        const data = encoding.base64toArrayBuffer(url);
        const name = `img-${this.realtime.identity.id}`;
        await this._setPicture({ data, name });
    }
    async setUserPictureFromAvatar(url) {
        this.debug('setUserPictureFromAvatar', Boolean(url));
        if (!url) {
            return;
        }
        this.userPicture = this.userPictureFromAvatar = url;
        await this._setPicture({ url });
    }
    clearUserPicture() {
        this.userPicture = this.userPictureFromAvatar = undefined;
    }
    async _setPicture(fileInput) {
        if (!this.connected) {
            return;
        }
        try {
            await this.realtime.setPicture(fileInput);
        }
        catch (e) {
            this.handleError(e, 'setPicture');
        }
    }
    async setState(update) {
        extend(this.globalState, update);
        return this._setState();
    }
    async _setState() {
        if (!this.connected) {
            return;
        }
        this.debug('setState:', this.globalState);
        try {
            setApolloSessionStorageData({ state: this.globalState });
            return await this.realtime.setState(this.globalState);
        }
        catch (e) {
            this.handleError(e, 'setState');
        }
    }
    resetState() {
        // dispatch first, so Redux store clears first
        // clear user
        this.$ngRedux.dispatch(reset());
        // clear presence
        this.$ngRedux.dispatch(clear());
        this.realtime = null;
        this.userPicture = null;
        this.connected = false;
        this.people = new PeopleCollection();
        this.forceRemovedPeople = new PeopleCollection();
        this.allJoinedPeople = new PeopleCollection();
    }
    async send(uid, message) {
        this.debug('send:', uid, message);
        const { identity } = this.realtime.membersMap[uid];
        try {
            // if chat - send to all identities with the same userId
            if (message.componentName === REALTIME_COMPONENT_NAMES.chat) {
                const targets = this.realtime.membersByUserId[identity.userId || identity.id];
                await Promise.all(targets.map(async (t) => this.realtime.sendMessage(t.identity, { message })));
                return;
            }
            await this.realtime.sendMessage(identity, { message });
        }
        catch (e) {
            this.handleError(e, 'send');
        }
    }
    async joinNewRooms(userId, token, roomIds) {
        const ids = roomIds.map(String);
        await this.realtime.joinAsProvider(ids, {
            token,
            userId: String(userId),
        });
        this.initData.roomIds.push(...ids);
    }
    forceRemove(person) {
        this.$ngRedux.dispatch(forceRemove(person.uuid));
        this.people.remove(person);
        this.forceRemovedPeople.add(person);
        this.emit(PnEvents.ForceRemove);
    }
    async stop(isLeavingPage = false) {
        this.connected = false;
        if (this.realtime) {
            if (this.isPatient()) {
                void API.room.onPatientLeft({
                    roomId: this.initData.roomIds[0],
                    name: this.globalState.username,
                    pid: this.globalState.pid,
                });
            }
            await this.realtime.leave(isLeavingPage);
            this.emit(PnEvents.Unsubscribe);
            this.resetState();
        }
        if (this.unsubscribe) {
            this.unsubscribe();
            this.unsubscribe = null;
        }
    }
    getRooms() {
        return get(this, 'realtime.rooms', []);
    }
    // @TODO: https://gitlab.com/doxyme/app/doxyme-frontend/-/merge_requests/3831#note_800735101
    async syncAllRooms() {
        if (!this.connected) {
            this.handleError(new Error("Sync all rooms isn't possible! No connection to realtime!"), 'syncAllRooms');
            return;
        }
        try {
            await this.realtime.refresh();
            this.mapMembersToPersons();
        }
        catch (e) {
            this.handleError(e, 'syncAllRooms');
        }
    }
    /**
     * Removes all users from the queue and refills with current ones
     */
    async refresh() {
        this.people.clear();
        this.$ngRedux.dispatch(clear());
        return this.syncAllRooms();
    }
    hasBadConnection() {
        return (this.globalState.connection &&
            ['unstable', 'poor'].includes(this.globalState.connection.level));
    }
    handleMessage({ uuid, message, role, channel, member, }) {
        // check server messages
        if (role === RealtimeRole.System) {
            this.emit(PnEvents.Message, {
                channel,
                message: {
                    ...message,
                    componentName: REALTIME_COMPONENT_NAMES.server,
                },
            });
            return;
        }
        const person = this.findOrCreateAllJoinedPerson(uuid, member);
        if (message && message.target === 'extension') {
            if (uuid === this.getUuid()) {
                // sender skip himself
                return;
            }
            this.$ngRedux.dispatch(setExtensionMessage(message.data));
            return;
        }
        if (!person) {
            this.handleError({
                error: new Error(`handleMessage: person not found - ${uuid}`),
                action: 'handleMessage',
            });
            return;
        }
        this.$rootScope.$apply(() => {
            this.emit(PnEvents.Message, {
                channel,
                message,
            }, person);
        });
    }
    findOrCreateAllJoinedPerson(id, member) {
        return (this.allJoinedPeople.findByUid(id) || this.createAllJoinedPerson(member));
    }
    createAllJoinedPerson(member) {
        if (!member) {
            return null;
        }
        const person = new PnPerson({
            currentUserId: this.getUserId(),
            uuid: member.identity.id,
            state: member.state,
            userPicture: member.picture,
            type: getPersonTypeFromIdentity(member.identity),
            roomId: Number.parseInt(member.roomId),
            member,
        });
        this.allJoinedPeople.add(person);
        return person;
    }
    addOrUpdatePerson(member, skipSound = false) {
        const person = this.updateOrCreateAllJoinedPerson(member);
        if (this.forceRemovedPeople.findByUid(person.uuid)) {
            return;
        }
        const updateObject = {
            type: person.type,
            roomId: person.roomId,
            uuid: person.uuid,
            state: person.state,
            userPicture: person.userPicture,
        };
        if (!this.people.findByUid(person.uuid)) {
            this.debug('addPerson', person.state, person);
            this.people.add(person);
            this.$ngRedux.dispatch(onJoin(updateObject, skipSound));
            this.people.models.sort((a, b) => a.state.checkedInAt - b.state.checkedInAt);
            this.emit(PnEvents.Join, person);
            return;
        }
        this.debug('updatePersonState', person.state, person);
        this.$ngRedux.dispatch(onStateUpdate(updateObject));
        this.emit(PnEvents.StateUpdate, person);
    }
    updateOrCreateAllJoinedPerson(member) {
        const person = this.allJoinedPeople.findByUid(member.identity.id);
        if (person) {
            person.member = member;
            person.setUserPicture(member.picture);
            person.changeState(member.state);
            return person;
        }
        return this.createAllJoinedPerson(member);
    }
    mapMembersToPersons() {
        if (!this.realtime) {
            return;
        }
        Object.values(this.realtime.membersMap).forEach((member) => {
            !isEmpty(member.state) && this.addOrUpdatePerson(member, true);
        });
    }
    removeMember(identity) {
        this.debug('onLeave', identity.id);
        const person = this.allJoinedPeople.findByUid(identity.id);
        if (!person) {
            return;
        }
        const hasBeenRemovedFromPeople = this.people.findByUid(identity.id);
        this.people.remove(person);
        this.$ngRedux.dispatch(onLeave(person.uuid));
        person.removeAllListeners();
        if (hasBeenRemovedFromPeople) {
            this.emit(PnEvents.Leave, person);
        }
    }
    handleError(error, action = 'handleError') {
        this.logError({ action, error });
        this.$rootScope.$emit(RootScopeEvents.REALTIME.ERROR, {
            error,
            action,
            identity: get(this, 'realtime.identity'),
        });
    }
    async initRealtime(role, roomIds, credentials, state = {}) {
        const getVendorLogLevel = () => this.growthbookFactory.getFeature('realtime_vendor_log_level').value;
        extend(this.globalState, state, { doxyVersion: this.doxyVersion });
        this.realtime = new Realtime(config.realtimeConfig.backendURL, getVendorLogLevel);
        this.setUpListeners();
        this.$rootScope.$emit(RootScopeEvents.REALTIME.INIT);
        try {
            await this.join(role, roomIds, credentials, this.globalState);
            setApolloSessionStorageData({ state: this.globalState });
        }
        catch (e) {
            this.handleError(e, 'initRealtime');
            throw e;
        }
        this.initData = { role, roomIds, credentials };
        this.connected = true;
        this.emit(PnEvents.Connected);
        this.mapMembersToPersons();
        this.$ngRedux.dispatch(subscribe({
            state: this.globalState,
            uuid: this.getUuid(),
            userId: this.getIdentityUserId(),
        }));
        this.$rootScope.$emit(RootScopeEvents.REALTIME.SUBSCRIBE, {
            uuid: this.getUuid(),
            roomIds,
        });
        this.debug('init finished');
    }
    async join(role, roomIds, credentials, state) {
        try {
            await this.realtime.join(role, roomIds, credentials, state);
        }
        catch (error) {
            if (error?.status?.statusCode === 403) {
                await this.realtime.join(role, roomIds, credentials, state);
                return;
            }
            throw error;
        }
    }
    async reinit() {
        try {
            const { role, roomIds, credentials } = this.initData;
            await this.join(role, roomIds, credentials, this.globalState);
            this.mapMembersToPersons();
        }
        catch (e) {
            this.handleError(e, 'reinit');
        }
    }
    setUpListeners() {
        this.unsubscribe = this.realtime.on('*', (event, type) => {
            this.debug('event:', type);
            const member = this.realtime.membersMap[event.from.id];
            switch (type) {
                case RealtimeEventType.Join: {
                    debugMembers(this.realtime.membersMap);
                    if (member && !isEmpty(member.state)) {
                        this.addOrUpdatePerson(member);
                    }
                    break;
                }
                case RealtimeEventType.SetState: {
                    debugMembers(this.realtime.membersMap);
                    if (member) {
                        this.addOrUpdatePerson(member, true);
                    }
                    break;
                }
                case RealtimeEventType.Leave:
                    realtimeDebugger.patientLeft(event.from);
                    this.removeMember(event.from);
                    break;
                case RealtimeEventType.Message:
                    this.handleMessage({
                        uuid: event.from.id,
                        role: event.from.role,
                        message: event.data.message,
                        channel: `channel--${event.roomId}`,
                        member,
                    });
                    break;
                case RealtimeEventType.Status:
                    if (event.data.status === RealtimeStatus.AccessDenied) {
                        this.emit(PnEvents.AccessDenied);
                        // currently we reconnect automatically only provider
                        if (this.initData.role === RealtimeRole.Provider) {
                            this.reinit();
                        }
                    }
                    break;
                case RealtimeEventType.PreJoin:
                    // do nothing
                    break;
                default:
                    console.info(`Unhandled realtime event: ${type}`, event);
                    break;
            }
        });
    }
}
